import React, { useEffect, useState } from "react";
import { TfiAngleDown, TfiAngleUp } from "react-icons/tfi";
import MenuImage from "../../../../../helper/images/menuimage.png";
import VegIcon from "../../../../../helper/svg/Veg.svg";
import { MdStar } from "react-icons/md";
import {
  getAllCategories,
  getRestaurantMenu,
} from "../../../../../api/user/restaurants";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../../../components/common/Pagination";
import NonVegIcon from "../../../../../helper/svg/NonVeg.svg";
import toast from "react-hot-toast";
import {
  getCart,
  addOrUpdateCartItem,
  removeCartItem,
  updateCartItemQuantity,
  clearCart,
  getCartPrice,
} from "../../../../../api/user/restaurants/cart";
import { useTranslation } from "../../../../../hooks/useTranslation";
import { ClipLoader } from "react-spinners";

const AccordionSection = ({
  title,
  children,
  index,
  openIndex,
  setOpenIndex,
}) => {
  const isOpen = openIndex === index;

  const toggleAccordion = () => {
    setOpenIndex(isOpen ? null : index);
  };

  return (
    <div className="py-[25px] border-b border-[#DDDDDD] ">
      <div
        className="flex justify-between text-xl font-semibold cursor-pointer"
        onClick={toggleAccordion}
      >
        {title}
        <span>
          {isOpen ? <TfiAngleUp size={20} /> : <TfiAngleDown size={20} />}
        </span>
      </div>
      {isOpen && <div>{children}</div>}
    </div>
  );
};

const Menu = ({
  restaurantId,
  reservationId,
  cartItems,
  setCartItems,
  menuOnlyMode,
}) => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 5;
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    categoryId: "",
  });
  const queryClient = useQueryClient();

  const [countMap, setCountMap] = useState({});
  const [notesMap, setNotesMap] = useState({});
  const [quantityLoading, setQuantityLoading] = useState({});
  const [ctaLoading, setCtaLoading] = useState({});
  const [inputQuantities, setInputQuantities] = useState({});
  const [isUpdatingQuantity, setIsUpdatingQuantity] = useState({});
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);
  const [modalQuantity, setModalQuantity] = useState("");
  const [showReplaceCartModal, setShowReplaceCartModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // Store the action to execute after confirmation

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["restaurant-menu-categories", restaurantId],
    queryFn: () => getAllCategories(restaurantId),
    keepPreviousData: true,
    enabled: !!restaurantId,
  });

  const { data: restaurantMenuData } = useQuery({
    queryKey: ["restaurant-menu", formData.categoryId, page, limit],
    queryFn: () => getRestaurantMenu(formData.categoryId, page, limit),
    keepPreviousData: true,
    enabled: !!formData.categoryId,
  });

  const categories = data?.data?.categories || [];
  const restaurantMenu = restaurantMenuData?.data?.items || [];
  const pagination = restaurantMenuData?.data?.pagination || {};

  const { data: cartData, isLoading: isCartLoading } = useQuery({
    queryKey: ["cart"],
    queryFn: getCart,
  });


  // useEffect(() => {

  //   if (cartData?.data?.items?.length === 0) {
  //     return;
  //   }
  //   const formatted = cartData?.data?.items?.map((item) => ({
  //     ...item.itemId,
  //     quantity: item.quantity,
  //     note: item.note || "",
  //     cartItemId: item._id
  //   }));
  //   setCartItems(formatted)

  // }, [cartData])

  useEffect(() => {
    if (cartData?.data?.items?.length > 0) {
      const formatted = cartData.data.items.map((item) => ({
        ...item.itemId,
        quantity: item.quantity,
        note: item.note || "",
        cartItemId: item._id,
      }));

      setCartItems(formatted);

      const counts = {};
      const notes = {};
      formatted.forEach((item) => {
        counts[item._id] = item.quantity;
        notes[item._id] = item.note;
      });
      setCountMap(counts);
      setNotesMap(notes);
    } else {
      setCartItems([]);
      setCountMap({});
      setNotesMap({});
      setInputQuantities({});
    }
  }, [cartData]);

  const addOrUpdateMutation = useMutation({
    mutationFn: addOrUpdateCartItem,
    onSuccess: () => queryClient.invalidateQueries(["cart"]),
  });

  const removeMutation = useMutation({
    mutationFn: removeCartItem,
    onSuccess: () => queryClient.invalidateQueries(["cart"]),
  });

  const updateQuantityMutation = useMutation({
    mutationFn: updateCartItemQuantity,
    onSuccess: () => {
      queryClient.invalidateQueries(["cart"]);
    },
    onError: (err) => {
      toast.error("Failed to update quantity");
    },
  });

  useEffect(() => {
    if (openIndex) {
      setFormData((prev) => ({
        ...prev,
        categoryId: openIndex,
      }));
    }
  }, [openIndex]);


  const handleAdd = (itemId) => {
    // Check if cart has items from a different restaurant
    const cartRestaurantId = cartData?.data?.restaurantId ? String(cartData.data.restaurantId) : null;
    const currentRestaurantId = restaurantId ? String(restaurantId) : null;
    const hasDifferentRestaurant = cartRestaurantId && cartRestaurantId !== currentRestaurantId && cartData?.data?.items?.length > 0;

    if (hasDifferentRestaurant) {
      // Show confirmation modal
      setPendingAction(() => () => {
        setQuantityLoading((prev) => ({ ...prev, [itemId]: "add" }));
        addOrUpdateMutation.mutate(
          { restaurantId, itemId, ...(reservationId && { reservationId }) },
          {
            onSuccess: () => {
              queryClient.invalidateQueries(["cart"]);
            },
            onError: (err) => {
              toast.error(t("restaurantmenu").failedAddItem);
            },
            onSettled: () => {
              setQuantityLoading((prev) => {
                const updated = { ...prev };
                delete updated[itemId];
                return updated;
              });
            },
          }
        );
      });
      setShowReplaceCartModal(true);
      return;
    }

    // show loader immediately
    setQuantityLoading((prev) => ({ ...prev, [itemId]: "add" }));

    addOrUpdateMutation.mutate(
      { restaurantId, itemId, ...(reservationId && { reservationId }) },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["cart"]);
        },
        onError: (err) => {
      toast.error(t("restaurantmenu").failedAddItem);
        },
        onSettled: () => {
          // hide loader after mutation finished
          setQuantityLoading((prev) => {
            const updated = { ...prev };
            delete updated[itemId];
            return updated;
          });
        },
      }
    );
  };

  const handleAddClick = (itemId) => {
    // Check if cart has items from a different restaurant
    const cartRestaurantId = cartData?.data?.restaurantId ? String(cartData.data.restaurantId) : null;
    const currentRestaurantId = restaurantId ? String(restaurantId) : null;
    const hasDifferentRestaurant = cartRestaurantId && cartRestaurantId !== currentRestaurantId && cartData?.data?.items?.length > 0;

    if (hasDifferentRestaurant) {
      // Show confirmation modal first
      setPendingAction(() => () => {
        setSelectedItemForModal(itemId);
        setModalQuantity("");
        setShowQuantityModal(true);
      });
      setShowReplaceCartModal(true);
      return;
    }

    // Show modal to ask for quantity
    setSelectedItemForModal(itemId);
    setModalQuantity("");
    setShowQuantityModal(true);
  };

  const handleConfirmAddToCart = () => {
    if (!selectedItemForModal) return;

    const quantityToAdd = parseInt(modalQuantity, 10);
    
    if (!quantityToAdd || quantityToAdd <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }

    // Check if cart has items from a different restaurant
    const cartRestaurantId = cartData?.data?.restaurantId ? String(cartData.data.restaurantId) : null;
    const currentRestaurantId = restaurantId ? String(restaurantId) : null;
    const hasDifferentRestaurant = cartRestaurantId && cartRestaurantId !== currentRestaurantId && cartData?.data?.items?.length > 0;

    if (hasDifferentRestaurant) {
      // Show confirmation modal first
      setPendingAction(() => () => {
        const currentQuantity = countMap[selectedItemForModal] || 0;
        const newTotalQuantity = currentQuantity + quantityToAdd;

        // Close modal
        setShowQuantityModal(false);
        setSelectedItemForModal(null);
        setModalQuantity("");

        // Show loader
        setCtaLoading((prev) => ({ ...prev, [selectedItemForModal]: true }));

        // Update cart with new total quantity
        updateQuantityMutation.mutate(
          {
            restaurantId,
            itemId: selectedItemForModal,
            quantity: newTotalQuantity,
            note: notesMap[selectedItemForModal] || "",
            ...(reservationId && { reservationId }),
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries(["cart"]);
              toast.success(`Added ${quantityToAdd} item(s) to cart`);
            },
            onError: (err) => {
              toast.error(err?.response?.data?.message || t("restaurantmenu").failedAddItems);
            },
            onSettled: () => {
              setCtaLoading((prev) => ({ ...prev, [selectedItemForModal]: false }));
            },
          }
        );
      });
      setShowReplaceCartModal(true);
      return;
    }

    const currentQuantity = countMap[selectedItemForModal] || 0;
    const newTotalQuantity = currentQuantity + quantityToAdd;

    // Close modal
    setShowQuantityModal(false);
    setSelectedItemForModal(null);
    setModalQuantity("");

    // Show loader
    setCtaLoading((prev) => ({ ...prev, [selectedItemForModal]: true }));

    // Update cart with new total quantity
    updateQuantityMutation.mutate(
      {
        restaurantId,
        itemId: selectedItemForModal,
        quantity: newTotalQuantity,
        note: notesMap[selectedItemForModal] || "",
        ...(reservationId && { reservationId }),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["cart"]);
          toast.success(`Added ${quantityToAdd} item(s) to cart`);
        },
        onError: (err) => {
      toast.error(err?.response?.data?.message || t("restaurantmenu").failedAddItems);
        },
        onSettled: () => {
          setCtaLoading((prev) => ({ ...prev, [selectedItemForModal]: false }));
        },
      }
    );
  };

  const handleCloseModal = () => {
    setShowQuantityModal(false);
    setSelectedItemForModal(null);
    setModalQuantity("");
  };

  const handleConfirmReplaceCart = () => {
    setShowReplaceCartModal(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleCancelReplaceCart = () => {
    setShowReplaceCartModal(false);
    setPendingAction(null);
  };

  const handleRemove = (itemId) => {
    setQuantityLoading((prev) => ({ ...prev, [itemId]: "remove" }));

    removeMutation.mutate(
      { restaurantId, itemId, ...(reservationId && { reservationId }) },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["cart"]);
        },
        onError: (err) => {
          toast.error("Failed to remove item");
        },
        onSettled: () => {
          setQuantityLoading((prev) => {
            const updated = { ...prev };
            delete updated[itemId];
            return updated;
          });
        },
      }
    );
  };


  const handleNotesChange = (itemId, note) => {
    setNotesMap((prev) => ({
      ...prev,
      [itemId]: note,
    }));
  };

  const isItemInCart = (itemId) => {
    return cartItems.some((item) => item.itemId === itemId);
  };

  const handleQuantityInputChange = (itemId, value) => {
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    setInputQuantities((prev) => ({
      ...prev,
      [itemId]: numericValue,
    }));
  };

  const handleQuantityInputBlur = (itemId) => {
    const inputValue = inputQuantities[itemId];
    const currentQuantity = countMap[itemId] || 0;

    // If input is empty or same as current, don't update
    if (!inputValue || inputValue === String(currentQuantity)) {
      setInputQuantities((prev) => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      return;
    }

    const newQuantity = parseInt(inputValue, 10) || 0;

    // Show loading state
    setIsUpdatingQuantity((prev) => ({ ...prev, [itemId]: true }));

    updateQuantityMutation.mutate(
      {
        restaurantId,
        itemId,
        quantity: newQuantity,
        note: notesMap[itemId] || "",
        ...(reservationId && { reservationId }),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["cart"]);
          setInputQuantities((prev) => {
            const updated = { ...prev };
            delete updated[itemId];
            return updated;
          });
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to update quantity");
          // Reset input to current quantity on error
          setInputQuantities((prev) => {
            const updated = { ...prev };
            delete updated[itemId];
            return updated;
          });
        },
        onSettled: () => {
          setIsUpdatingQuantity((prev) => {
            const updated = { ...prev };
            delete updated[itemId];
            return updated;
          });
        },
      }
    );
  };

  const handleQuantityInputKeyDown = (e, itemId) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      // Reset input on escape
      setInputQuantities((prev) => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      e.target.blur();
    }
  };

  const selectedMenuItem = restaurantMenu?.find(item => item._id === selectedItemForModal);

  return (
    <>
      {/* Replace Cart Confirmation Modal */}
      {showReplaceCartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[20px] w-full max-w-md p-6 md:p-8 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold text-black">
                {t("restaurantmenu").replaceCartTitle || "Replace Cart Items?"}
              </h3>
              <button
                onClick={handleCancelReplaceCart}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="mb-6">
              <p className="text-base text-gray-700 mb-4">
                {t("restaurantmenu").replaceCartMessage || "You have items from another restaurant in your cart. Adding items from this restaurant will replace all existing items in your cart."}
              </p>
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  <strong>{t("restaurantmenu").replaceCartWarning || "Warning:"}</strong> {t("restaurantmenu").replaceCartWarningText || "This action cannot be undone."}
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCancelReplaceCart}
                className="flex-1 py-3 px-4 rounded-[10px] bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors"
              >
                {t("restaurantmenu").cancel || "Cancel"}
              </button>
              <button
                onClick={handleConfirmReplaceCart}
                className="flex-1 py-3 px-4 rounded-[10px] bg-[#4CAF50] text-white font-semibold hover:bg-[#209b25] transition-colors"
              >
                {t("restaurantmenu").replaceCart || "Replace Cart"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Input Modal */}
      {showQuantityModal && selectedMenuItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[20px] w-full max-w-md p-6 md:p-8 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold text-black">
                {t("restaurantmenu").addToCart}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-3 mb-4">
                {selectedMenuItem?.images?.length > 0 ? (
                  <img
                    src={selectedMenuItem.images[0]}
                    alt={selectedMenuItem.itemName}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <img
                    src={MenuImage}
                    alt="Menu"
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                )}
                <div>
                  <h4 className="text-lg font-semibold text-black">
                    {selectedMenuItem.itemName}
                  </h4>
                  <p className="text-base text-gray-600">
                    ₾{selectedMenuItem.price}
                  </p>
                </div>
              </div>

              {countMap[selectedItemForModal] > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    {t("restaurantmenu").currentQuantityInCart || "Current quantity in cart:"} <span className="font-semibold text-black">{countMap[selectedItemForModal]}</span>
                  </p>
                </div>
              )}

              <label className="block text-sm font-medium text-gray-700 mb-2">
                 {t("restaurantmenu").quantityToAdd || "Quantity to add"}
              </label>
              <input
                type="number"
                min="1"
                value={modalQuantity}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setModalQuantity(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmAddToCart();
                  } else if (e.key === 'Escape') {
                    handleCloseModal();
                  }
                }}
                placeholder={t("restaurantmenu").enterQuantity || "Enter quantity"}
                className="w-full border border-gray-300 rounded-[10px] p-4 text-base focus:outline-none focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent"
                autoFocus
              />

              {/* {modalQuantity && parseInt(modalQuantity, 10) > 0 && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-700">
                    Total quantity will be: <span className="font-bold text-[#4CAF50] text-lg">
                      {(countMap[selectedItemForModal] || 0) + parseInt(modalQuantity, 10)}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    (Current: {countMap[selectedItemForModal] || 0} + New: {parseInt(modalQuantity, 10)})
                  </p>
                </div>
              )} */}

              {modalQuantity && parseInt(modalQuantity, 10) > 0 && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-700">
                    {t("restaurantmenu").totalQuantityWillBe || "Total quantity will be:"} <span className="font-bold text-[#4CAF50] text-lg">
                      {(countMap[selectedItemForModal] || 0) + parseInt(modalQuantity, 10)}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    ({t("restaurantmenu").current || "Current"}: {countMap[selectedItemForModal] || 0} + {t("restaurantmenu").new || "New"}: {parseInt(modalQuantity, 10)})
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCloseModal}
                className="flex-1 py-3 px-4 rounded-[10px] bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors"
              >
                {t("restaurantmenu").cancel}
              </button>
              <button
                onClick={handleConfirmAddToCart}
                disabled={!modalQuantity || parseInt(modalQuantity, 10) <= 0}
                className="flex-1 py-3 px-4 rounded-[10px] bg-[#4CAF50] text-white font-semibold hover:bg-[#209b25] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("restaurantmenu").addToCart}
              </button>
            </div>
          </div>
        </div>
      )}

      {categories?.length > 0 ? (
        categories?.map((item) => {
          return (
            <AccordionSection
              index={item?._id}
              openIndex={openIndex}
              setOpenIndex={setOpenIndex}
              title={`${item?.category} (${item?.totalMenuItems})`}
            >
              {restaurantMenu?.length > 0 &&
                restaurantMenu?.map((item) => {
                  return (
                    <>
                      <div className="lg:items-center items-start justify-between grid lg:grid-cols-[1fr_auto] grid-cols-1 gap-[24px] bg-white rounded-[5px] lg:py-[10px] lg:pl-[11px] lg:pr-[20px] p-[15px] mt-[20px] menu-shadow">
                        <div className="flex lg:gap-[47px] gap-[10px] lg:items-center  lg:pr-[24px] ">
                          <img
                            src={
                              item?.images?.length > 0
                                ? `${item.images[0]}`
                                : MenuImage
                            }
                            className="md:max-w-[300px] max-w-[91px] xl:h-full rounded-[5px] object-cover"
                            alt="Menu"
                          />
                          <div className="">
                            {item?.foodType === "veg" ? (
                              <img src={VegIcon} alt="Veg Icon" />
                            ) : (
                              <img src={NonVegIcon} alt="Veg Icon" />
                            )}

                            <h3 className="font-semibold lg:text-[30px] md:text-[25px] text-lg text-black md:my-[15px] mt-[5px]">
                              {item?.itemName}
                            </h3>

                            <div className="flex items-center lg:gap-[37px] gap-[6px] md:mt-3 mt-[5px] max-w-[259px]">
                              <span className="lg:text-[22px] text-base font-medium text-black">
                                ₾{item?.price}
                              </span>
                              <div className="flex items-center md:gap-[15px] gap-[9px]">
                                <div className="flex items-center">
                                  {[...Array(5)].map((_, i) => (
                                    <MdStar
                                      key={i}
                                      size={21}
                                      className="text-[#FF9D00]"
                                    />
                                  ))}
                                </div>
                                <p className="text-[#4CAF50] text-[14px] font-medium">
                                  4.3 (7)
                                </p>
                              </div>
                            </div>

                            <p className="md:text-lg text-[15px] text-[#848484] md:mt-[19px] mt-[15px] md:leading-[30px] leading-6">
                              {item?.description}
                            </p>
                          </div>
                        </div>

                        {/* {reservationId && ( */}
                        <div>
                          <div className="grid grid-cols-[auto_1fr]  gap-[10px] items-center justify-between">
                            <span className="text-lg text-black">
                              {t("restaurantmenu".selectQuantity)}
                            </span>
                            <div className="w-full ml-auto">
                              <div className="flex  items-center justify-between border border-gray-300 rounded-full px-4 py-2 ">
                                <button
                                  onClick={() => handleRemove(item._id)}
                                  className="text-lg px-2 text-gray-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
                                  disabled={Boolean(quantityLoading[item._id]) || isUpdatingQuantity[item._id]}
                                >
                                  {quantityLoading[item._id] === "remove" ? (
                                    <ClipLoader size={16} color="#4CAF50" />
                                  ) : (
                                    "–"
                                  )}
                                </button>

                                {inputQuantities[item._id] !== undefined ? (
                                  <input
                                    type="text"
                                    value={inputQuantities[item._id]}
                                    onChange={(e) => handleQuantityInputChange(item._id, e.target.value)}
                                    onBlur={() => handleQuantityInputBlur(item._id)}
                                    onKeyDown={(e) => handleQuantityInputKeyDown(e, item._id)}
                                    className="mx-4 w-16 text-center text-base border-none outline-none focus:ring-2 focus:ring-[#4CAF50] rounded px-2 py-1"
                                    autoFocus
                                    disabled={isUpdatingQuantity[item._id]}
                                  />
                                ) : (
                                  <span
                                    className="mx-4 text-base cursor-pointer hover:text-[#4CAF50] transition-colors min-w-[24px] text-center"
                                    onClick={() => {
                                      setInputQuantities((prev) => ({
                                        ...prev,
                                        [item._id]: String(countMap[item._id] || 0),
                                      }));
                                    }}
                                  >
                                    {isUpdatingQuantity[item._id] ? (
                                      <ClipLoader size={16} color="#4CAF50" />
                                    ) : (
                                      countMap[item._id] || 0
                                    )}
                                  </span>
                                )}

                                <button
                                  onClick={() => handleAdd(item._id)}
                                  className="text-lg px-2 text-gray-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
                                  disabled={Boolean(quantityLoading[item._id]) || isUpdatingQuantity[item._id]}
                                >
                                  {quantityLoading[item._id] === "add" ? (
                                    <ClipLoader size={16} color="#4CAF50" />
                                  ) : (
                                    "+"
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* <button
                              onClick={() => handleAddToCart(item._id)}
                              className="mt-[17px] mb-5 w-full rounded-[2px] bg-[#4CAF50] 2xl:text-lg text-base text-white text-center font-bold 2xl:py-[14px] 2xl:px-[20px] p-3 green-shadow hover:bg-[#209b25]"
                            >
                              {isItemInCart(item._id) ? t("restaurantmenu").added : t("restaurantmenu").addToCart}
                            </button> */}



                          <button
                            onClick={() => handleAddClick(item._id)}
                            className="mt-[17px] mb-5 w-full rounded-[2px] bg-[#4CAF50] 2xl:text-lg text-base text-white text-center font-bold 2xl:py-[14px] 2xl:px-[20px] p-3 green-shadow hover:bg-[#209b25] disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={ctaLoading[item._id] || isUpdatingQuantity[item._id]}
                          >
                            {ctaLoading[item._id]
                              ? t("header").loading
                              : t("restaurantmenu").addToCart}
                          </button>
                          <textarea
                            className="w-full min-h-[115px] border border-[#DDDDDD] rounded-[10px] p-[24px] shadow-sm focus:outline-none focus:border-[#4CAF50]"
                           placeholder={t("restaurantmenu").notesPlaceholder || 'e.g., "No pepper", "Extra cheese"'}
                            value={notesMap[item._id] || ""}
                            onChange={(e) =>
                              handleNotesChange(item._id, e.target.value)
                            }
                          ></textarea>
                        </div>
                        {/* )} */}
                      </div>
                    </>
                  );
                })}

              {restaurantMenu?.length > 0 && (
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  setPage={setPage}
                />
              )}
            </AccordionSection>
          );
        })
      ) : (
        <div className="flex justify-center mt-10">
          <div className=" text-[#856404] px-6 py-3 rounded-md   text-lg font-semibold tracking-wide">
            {t("restaurantmenu".noMenuItemsFound)}
          </div>
        </div>
      )}
    </>
  );
};

export default Menu;
